/**
 * MouseSimulation - Shared deterministic mouse enemy logic.
 *
 * Used by GameRoom (multiplayer), LocalGameServer (offline),
 * and GameScene (client-side prediction for movement/falling only).
 *
 * Movement is deterministic: 1 tile per tick, turns at walls or step-downs.
 * Falling syncs with global tick like blocks and players.
 * Combat is server-authoritative (server/LocalGameServer only).
 *
 * Standalone tick functions are exported for use by chunk-based server logic.
 * The MouseSimulation class wraps them for backward-compatible flat-list usage.
 */

import {
    CHUNK_SIZE,
    MOUSE_DAMAGE,
    PLAYER_HEIGHT_STANDING,
    PLAYER_HEIGHT_CROUCHING,
    SWORD_OFFSET,
    TileType,
    RodentType,
    NestAbsorption,
    NestRespawn,
    getCreatureAI,
    getCreatureSize,
    getCreatureBodyTiles,
    CreatureSize,
} from './constants';
import { SimulationPlayer, ActiveSlash } from './GameSimulation';
import { isPassable } from './physics';

// ============================================================
// MOUSE ENTITY
// ============================================================

export interface MouseEntity {
    id: number;
    x: number;
    y: number;
    facing: number;   // 1 or -1 (direction of horizontal movement)
    alive: boolean;
    rodentType: string;  // RodentType enum value or creature ID (e.g. "soweli_40", "waso_87")
    // Squirrel wall-climbing state
    wallDir?: number;    // 0 = on floor/ceiling, 1 or -1 = on right/left wall
    vertDir?: number;    // 1 = moving down, -1 = moving up (for wall movement)
}

// ============================================================
// MOUSE EVENTS (produced by combat tick)
// ============================================================

export type MouseEvent =
    | { type: 'mouseDied'; mouseId: number }
    | { type: 'mousePlayerDamage'; playerId: string; hp: number }
    | { type: 'mousePlayerDeath'; playerId: string; deathX: number; deathY: number };

// ============================================================
// CHUNK KEY HELPER
// ============================================================

export function mouseChunkKey(x: number, y: number): string {
    return `${Math.floor(x / CHUNK_SIZE)},${Math.floor(y / CHUNK_SIZE)}`;
}

// ============================================================
// STANDALONE TICK FUNCTIONS
// Operate on an arbitrary array of mice — used by GameRoom
// for per-chunk simulation without needing a MouseSimulation instance.
// ============================================================

function canMouseMoveTo(x: number, y: number, getTile: (x: number, y: number) => number): boolean {
    const frontTile = getTile(x, y) as TileType;
    if (!isPassable(frontTile)) return false;
    const groundTile = getTile(x, y + 1) as TileType;
    return !isPassable(groundTile);
}

// ============================================================
// MULTI-TILE HELPERS
// ============================================================

/** Check if ALL body tiles of a multi-tile creature at (x,y) are in passable world tiles. */
function allBodyTilesPassable(
    x: number, y: number, facing: number, size: CreatureSize,
    getTile: (x: number, y: number) => number,
): boolean {
    const tiles = getCreatureBodyTiles(x, y, facing, size);
    for (const t of tiles) {
        if (!isPassable(getTile(t.tx, t.ty) as TileType)) return false;
    }
    return true;
}

/** Check if a multi-tile creature at (x,y) has ground beneath at least one bottom-row body tile.
 *  Used for airborne detection (any foot grounded = not falling). */
function hasGroundBelow(
    x: number, y: number, facing: number, size: CreatureSize,
    getTile: (x: number, y: number) => number,
): boolean {
    // Bottom row of the body = row index size.height-1, world y = y
    for (let c = 0; c < size.width; c++) {
        const col = facing >= 0 ? c : (size.width - 1 - c);
        if (size.body[size.height - 1][col] > 0) {
            // This column has a body tile on the bottom row — check ground below
            if (!isPassable(getTile(x + c, y + 1) as TileType)) return true;
        }
    }
    return false;
}

/** Check if ALL bottom-row body tiles of a multi-tile creature have solid ground below them.
 *  Used for movement validation — every foot must be supported at the destination. */
function allFeetGrounded(
    x: number, y: number, facing: number, size: CreatureSize,
    getTile: (x: number, y: number) => number,
): boolean {
    for (let c = 0; c < size.width; c++) {
        const col = facing >= 0 ? c : (size.width - 1 - c);
        if (size.body[size.height - 1][col] > 0) {
            if (isPassable(getTile(x + c, y + 1) as TileType)) return false;
        }
    }
    return true;
}

/** Check if a multi-tile creature can walk to new position: all body passable + all feet grounded. */
function canMultiTileMoveTo(
    x: number, y: number, facing: number, size: CreatureSize,
    getTile: (x: number, y: number) => number,
): boolean {
    return allBodyTilesPassable(x, y, facing, size, getTile) &&
           allFeetGrounded(x, y, facing, size, getTile);
}

/** Check if a multi-tile creature can fly/swim to new position: all body tiles passable. */
function canMultiTileFlyTo(
    x: number, y: number, facing: number, size: CreatureSize,
    getTile: (x: number, y: number) => number,
): boolean {
    return allBodyTilesPassable(x, y, facing, size, getTile);
}

/** Check if all body tiles of a swimming creature are in water. */
function allBodyTilesInWater(
    x: number, y: number, facing: number, size: CreatureSize,
    getTile: (x: number, y: number) => number,
): boolean {
    const tiles = getCreatureBodyTiles(x, y, facing, size);
    for (const t of tiles) {
        const tile = getTile(t.tx, t.ty) as TileType;
        if (tile !== TileType.WATER && tile !== TileType.WATER_SURFACE) return false;
    }
    return true;
}

/** Kill any mouse whose tile is solid (or fish out of water). Returns death events. Does NOT filter the array.
 *  For multi-tile creatures, checks ALL body tiles. */
export function tickMiceSuffocation(mice: MouseEntity[], getTile: (x: number, y: number) => number): MouseEvent[] {
    const events: MouseEvent[] = [];
    for (const mouse of mice) {
        if (!mouse.alive) continue;
        const ai = getCreatureAI(mouse.rodentType);
        const size = getCreatureSize(mouse.rodentType);
        const isMultiTile = size.width > 1 || size.height > 1;

        let shouldDie = false;
        if (isMultiTile) {
            // Multi-tile: check all body tiles
            const bodyTiles = getCreatureBodyTiles(mouse.x, mouse.y, mouse.facing, size);
            if (ai === 'swim') {
                shouldDie = bodyTiles.some(t => {
                    const tile = getTile(t.tx, t.ty) as TileType;
                    return tile !== TileType.WATER && tile !== TileType.WATER_SURFACE;
                });
            } else if (ai === 'fly') {
                shouldDie = bodyTiles.some(t => {
                    const tile = getTile(t.tx, t.ty) as TileType;
                    return !isPassable(tile) && tile !== TileType.WATER && tile !== TileType.WATER_SURFACE
                        && tile !== TileType.LADDER && tile !== TileType.LEAVES && tile !== TileType.STICK
                        && tile !== TileType.KASI_LINJA;
                });
            } else {
                shouldDie = bodyTiles.some(t => !isPassable(getTile(t.tx, t.ty) as TileType));
            }
        } else {
            // 1×1 creatures: original single-tile check
            const tile = getTile(mouse.x, mouse.y) as TileType;
            if (ai === 'swim') {
                shouldDie = tile !== TileType.WATER && tile !== TileType.WATER_SURFACE;
            } else if (ai === 'fly') {
                shouldDie = !isPassable(tile) && tile !== TileType.WATER && tile !== TileType.WATER_SURFACE
                    && tile !== TileType.LADDER && tile !== TileType.LEAVES && tile !== TileType.STICK
                    && tile !== TileType.KASI_LINJA;
            } else {
                shouldDie = !isPassable(tile);
            }
        }

        if (shouldDie) {
            mouse.alive = false;
            events.push({ type: 'mouseDied', mouseId: mouse.id });
        }
    }
    return events;
}

/** Move grounded mice 1 tile forward, turning at walls/step-downs. Mutates in place.
 *  Returns nest absorptions for rodents that hit a RATS_NEST block.
 *  Multi-tile creatures use width-aware movement checks. */
export function tickMiceMovement(mice: MouseEntity[], getTile: (x: number, y: number) => number): NestAbsorption[] {
    const absorptions: NestAbsorption[] = [];
    for (const mouse of mice) {
        if (!mouse.alive) continue;

        const size = getCreatureSize(mouse.rodentType);
        const isMultiTile = size.width > 1 || size.height > 1;

        let absorption: NestAbsorption | null = null;
        const ai = getCreatureAI(mouse.rodentType);

        if (isMultiTile) {
            // Multi-tile creatures: simplified walk/fly/swim using bounding box checks
            switch (ai) {
                case 'fly':
                    absorption = tickMultiTileBirdMovement(mouse, size, getTile);
                    break;
                case 'swim':
                    absorption = tickMultiTileFishMovement(mouse, size, getTile);
                    break;
                case 'walk':
                default:
                    absorption = tickMultiTileWalkMovement(mouse, size, getTile);
                    break;
            }
        } else {
            // 1×1 creatures: original AI dispatch
            switch (ai) {
                case 'stair':
                    absorption = tickRatMovement(mouse, getTile);
                    break;
                case 'cling':
                    absorption = tickSquirrelMovement(mouse, getTile);
                    break;
                case 'fly':
                    absorption = tickBirdMovement(mouse, getTile);
                    break;
                case 'swim':
                    absorption = tickFishMovement(mouse, getTile);
                    break;
                case 'walk':
                default:
                    absorption = tickBasicMouseMovement(mouse, getTile);
                    break;
            }
        }
        if (absorption) {
            mouse.alive = false;
            absorptions.push(absorption);
        }
    }
    return absorptions;
}

/** Basic mouse movement: walk forward, turn at walls/edges. Returns absorption if hitting RATS_NEST. */
function tickBasicMouseMovement(mouse: MouseEntity, getTile: (x: number, y: number) => number): NestAbsorption | null {
    // Airborne mice don't walk — they fall straight down
    const groundTile = getTile(mouse.x, mouse.y + 1) as TileType;
    if (isPassable(groundTile)) return null;

    // Try forward
    const nextX = mouse.x + mouse.facing;
    const frontTile = getTile(nextX, mouse.y) as TileType;

    // Check if hitting a RATS_NEST wall → absorb instead of turning
    if (frontTile === TileType.RATS_NEST) {
        return { mouseId: mouse.id, nestX: nextX, nestY: mouse.y, rodentType: mouse.rodentType, facing: mouse.facing };
    }

    if (canMouseMoveTo(nextX, mouse.y, getTile)) {
        mouse.x = nextX;
        return null;
    }

    // Turn around and try the other direction
    mouse.facing = -mouse.facing;
    const reverseX = mouse.x + mouse.facing;
    const reverseFrontTile = getTile(reverseX, mouse.y) as TileType;

    // Check reverse direction for RATS_NEST too
    if (reverseFrontTile === TileType.RATS_NEST) {
        return { mouseId: mouse.id, nestX: reverseX, nestY: mouse.y, rodentType: mouse.rodentType, facing: mouse.facing };
    }

    if (canMouseMoveTo(reverseX, mouse.y, getTile)) {
        mouse.x = reverseX;
    }
    // Otherwise stay put (boxed in)
    return null;
}

/**
 * Rat movement: walks along ground like mouse, but can climb 1-tile stairs up and down.
 * - When hitting a wall: if wall is exactly 1 tall with air above both rat and wall-top, step up.
 * - When reaching an edge: if block below the gap is solid, step down.
 * - Otherwise turn around.
 */
function tickRatMovement(mouse: MouseEntity, getTile: (x: number, y: number) => number): NestAbsorption | null {
    // Airborne rats don't walk — they fall straight down
    const groundTile = getTile(mouse.x, mouse.y + 1) as TileType;
    if (isPassable(groundTile)) return null;

    const nextX = mouse.x + mouse.facing;
    const frontTile = getTile(nextX, mouse.y) as TileType;
    const frontGroundTile = getTile(nextX, mouse.y + 1) as TileType;

    // Check if hitting a RATS_NEST wall → absorb instead of climbing/turning
    if (frontTile === TileType.RATS_NEST) {
        return { mouseId: mouse.id, nestX: nextX, nestY: mouse.y, rodentType: mouse.rodentType, facing: mouse.facing };
    }

    // Case 1: Flat forward (air at rat level, solid below)
    if (isPassable(frontTile) && !isPassable(frontGroundTile)) {
        mouse.x = nextX;
        return null;
    }

    // Case 2: Wall ahead — try stair up
    if (!isPassable(frontTile)) {
        const aboveWall = getTile(nextX, mouse.y - 1) as TileType;
        const aboveRat = getTile(mouse.x, mouse.y - 1) as TileType;
        if (isPassable(aboveWall) && isPassable(aboveRat)) {
            mouse.x = nextX;
            mouse.y = mouse.y - 1;
            return null;
        }
    }

    // Case 3: Edge ahead — try stair down
    if (isPassable(frontTile) && isPassable(frontGroundTile)) {
        const belowDrop = getTile(nextX, mouse.y + 2) as TileType;
        if (!isPassable(belowDrop)) {
            mouse.x = nextX;
            mouse.y = mouse.y + 1;
            return null;
        }
    }

    // Can't move forward — turn around and try
    mouse.facing = -mouse.facing;
    const revX = mouse.x + mouse.facing;
    const revFront = getTile(revX, mouse.y) as TileType;
    const revGround = getTile(revX, mouse.y + 1) as TileType;

    // Check reverse direction for RATS_NEST too
    if (revFront === TileType.RATS_NEST) {
        return { mouseId: mouse.id, nestX: revX, nestY: mouse.y, rodentType: mouse.rodentType, facing: mouse.facing };
    }

    // Flat reverse
    if (isPassable(revFront) && !isPassable(revGround)) {
        mouse.x = revX;
        return null;
    }

    // Stair up reverse
    if (!isPassable(revFront)) {
        const aboveWallRev = getTile(revX, mouse.y - 1) as TileType;
        const aboveRatRev = getTile(mouse.x, mouse.y - 1) as TileType;
        if (isPassable(aboveWallRev) && isPassable(aboveRatRev)) {
            mouse.x = revX;
            mouse.y = mouse.y - 1;
            return null;
        }
    }

    // Stair down reverse
    if (isPassable(revFront) && isPassable(revGround)) {
        const belowDropRev = getTile(revX, mouse.y + 2) as TileType;
        if (!isPassable(belowDropRev)) {
            mouse.x = revX;
            mouse.y = mouse.y + 1;
            return null;
        }
    }
    // Boxed in — stay put
    return null;
}

/**
 * Squirrel movement: walks along all surfaces (floor, walls, ceiling).
 *
 * State: wallDir indicates which surface the squirrel clings to.
 *   wallDir=0:  on floor (moving horizontally with facing) or ceiling
 *   wallDir=1:  on a wall to the RIGHT of the squirrel (squirrel moves vertically)
 *   wallDir=-1: on a wall to the LEFT of the squirrel (squirrel moves vertically)
 *
 * vertDir: when on a wall (wallDir != 0), 1=moving down, -1=moving up.
 *          when on floor, vertDir=1 (gravity side).
 *          when on ceiling, vertDir=-1.
 *
 * Movement: advance 1 tile along the surface. When the surface ends,
 * make a diagonal move and rotate 90 degrees to continue along the
 * next surface.
 */
function tickSquirrelMovement(mouse: MouseEntity, getTile: (x: number, y: number) => number): NestAbsorption | null {
    // Initialize wall state if not set
    if (mouse.wallDir === undefined) mouse.wallDir = 0;
    if (mouse.vertDir === undefined) mouse.vertDir = 1;

    // If squirrel is in the air (no adjacent surface), let it fall via tickMiceFalling
    if (!squirrelHasSurface(mouse, getTile)) return null;

    // Check if the surface the squirrel is currently clinging to is a RATS_NEST
    const nestAbsorption = checkSquirrelNestSurface(mouse, getTile);
    if (nestAbsorption) return nestAbsorption;

    if (mouse.wallDir === 0) {
        // On floor or ceiling — moving horizontally
        tickSquirrelHorizontal(mouse, getTile);
    } else {
        // On a wall — moving vertically
        tickSquirrelVertical(mouse, getTile);
    }
    return null;
}

/** Check if squirrel has a surface to cling to based on its wallDir/vertDir. */
function squirrelHasSurface(mouse: MouseEntity, getTile: (x: number, y: number) => number): boolean {
    const wd = mouse.wallDir ?? 0;
    const vd = mouse.vertDir ?? 1;
    if (wd === 0) {
        // Floor: solid at (x, y+1) when vertDir=1, ceiling: solid at (x, y-1) when vertDir=-1
        const surfaceY = vd === 1 ? mouse.y + 1 : mouse.y - 1;
        return !isPassable(getTile(mouse.x, surfaceY) as TileType);
    } else {
        // Wall: solid at (x + wallDir, y)
        return !isPassable(getTile(mouse.x + wd, mouse.y) as TileType);
    }
}

/** Check if the squirrel's current surface is a RATS_NEST block. */
function checkSquirrelNestSurface(mouse: MouseEntity, getTile: (x: number, y: number) => number): NestAbsorption | null {
    const wd = mouse.wallDir ?? 0;
    const vd = mouse.vertDir ?? 1;
    let surfaceX: number, surfaceY: number;
    if (wd === 0) {
        // Floor (vd=1): surface at (x, y+1). Ceiling (vd=-1): surface at (x, y-1).
        surfaceX = mouse.x;
        surfaceY = vd === 1 ? mouse.y + 1 : mouse.y - 1;
    } else {
        // Wall: surface at (x + wallDir, y)
        surfaceX = mouse.x + wd;
        surfaceY = mouse.y;
    }
    if ((getTile(surfaceX, surfaceY) as TileType) === TileType.RATS_NEST) {
        return {
            mouseId: mouse.id,
            nestX: surfaceX, nestY: surfaceY,
            rodentType: mouse.rodentType,
            facing: mouse.facing,
            wallDir: mouse.wallDir,
            vertDir: mouse.vertDir,
        };
    }
    return null;
}

/** Squirrel moving horizontally (on floor or ceiling). */
function tickSquirrelHorizontal(mouse: MouseEntity, getTile: (x: number, y: number) => number): void {
    const vd = mouse.vertDir ?? 1; // 1 = floor, -1 = ceiling
    const nextX = mouse.x + mouse.facing;
    const surfaceY = vd === 1 ? mouse.y + 1 : mouse.y - 1; // y of surface below/above

    const frontTile = getTile(nextX, mouse.y) as TileType;
    const frontSurface = getTile(nextX, surfaceY) as TileType;

    // Case 1: flat forward — air ahead, surface continues
    if (isPassable(frontTile) && !isPassable(frontSurface)) {
        mouse.x = nextX;
        return;
    }

    // Case 2: hit a wall ahead — transition to wall-climbing
    // Wall is at (nextX, mouse.y). Squirrel moves diagonal onto the wall
    // and starts climbing vertically (opposite to vertDir on that wall).
    if (!isPassable(frontTile)) {
        // Move to the tile in front of the wall (stay at mouse.x) and start climbing
        // The wall is on the side = mouse.facing. Start moving upward if on floor, downward if ceiling.
        mouse.wallDir = mouse.facing;
        mouse.vertDir = -vd; // on floor (vd=1) → climb up (-1); on ceiling (vd=-1) → climb down (1)
        // Move one tile in the new vertical direction
        const nextY = mouse.y + mouse.vertDir;
        const nextTile = getTile(mouse.x, nextY) as TileType;
        const wallStill = getTile(mouse.x + mouse.wallDir, nextY) as TileType;
        if (isPassable(nextTile) && !isPassable(wallStill)) {
            mouse.y = nextY;
        }
        return;
    }

    // Case 3: surface ends (gap ahead) — wrap around the edge
    // Diagonal move: forward one, into the surface direction one
    if (isPassable(frontTile) && isPassable(frontSurface)) {
        mouse.x = nextX;
        mouse.y = mouse.y + vd; // move toward where surface was
        // Now cling to the wall behind us (opposite of facing)
        mouse.wallDir = -mouse.facing;
        mouse.vertDir = vd === 1 ? 1 : -1; // continue in same general direction
        // facing stays the same (still "forward" along original direction)
        return;
    }
}

/** Squirrel moving vertically (on a wall). */
function tickSquirrelVertical(mouse: MouseEntity, getTile: (x: number, y: number) => number): void {
    const wd = mouse.wallDir!; // 1 or -1: wall is at x+wd
    const vd = mouse.vertDir!; // 1=down, -1=up
    const nextY = mouse.y + vd;

    const frontTile = getTile(mouse.x, nextY) as TileType;
    const wallTile = getTile(mouse.x + wd, nextY) as TileType;

    // Case 1: clear path, wall continues — advance vertically
    if (isPassable(frontTile) && !isPassable(wallTile)) {
        mouse.y = nextY;
        return;
    }

    // Case 2: hit floor/ceiling — transition to horizontal movement
    if (!isPassable(frontTile)) {
        // The surface we hit is floor (if moving down) or ceiling (if moving up)
        mouse.wallDir = 0;
        mouse.vertDir = vd; // remember which side the surface is on
        // Set facing to move AWAY from the wall
        mouse.facing = -wd;
        // Try to move horizontally one tile
        const hNextX = mouse.x + mouse.facing;
        const hFrontTile = getTile(hNextX, mouse.y) as TileType;
        const hSurface = getTile(hNextX, mouse.y + vd) as TileType;
        if (isPassable(hFrontTile) && !isPassable(hSurface)) {
            mouse.x = hNextX;
        }
        return;
    }

    // Case 3: wall ends (gap) — wrap around the wall's edge
    // Diagonal: move vertically one + move toward wall one
    if (isPassable(frontTile) && isPassable(wallTile)) {
        mouse.y = nextY;
        mouse.x = mouse.x + wd; // move into where wall was
        // Now on floor/ceiling
        mouse.wallDir = 0;
        mouse.vertDir = -vd; // surface is behind the direction we came from
        mouse.facing = wd;   // continue along the wall's direction
        return;
    }
}

// ============================================================
// MULTI-TILE MOVEMENT FUNCTIONS
// ============================================================

/** Multi-tile ground walk: move forward, turn at obstacles. No nest absorption for large creatures. */
function tickMultiTileWalkMovement(
    mouse: MouseEntity, size: CreatureSize,
    getTile: (x: number, y: number) => number,
): NestAbsorption | null {
    // Airborne multi-tile creatures don't walk — they fall via tickMiceFalling
    if (!hasGroundBelow(mouse.x, mouse.y, mouse.facing, size, getTile)) return null;

    // Try forward
    const nextX = mouse.x + mouse.facing;
    if (canMultiTileMoveTo(nextX, mouse.y, mouse.facing, size, getTile)) {
        mouse.x = nextX;
        return null;
    }

    // Turn around in place — don't move this tick.
    // Keeps the same bounding-box footprint so the creature doesn't
    // overhang ledge edges or visually pivot around one end.
    mouse.facing = -mouse.facing;
    return null;
}

/** Multi-tile bird movement: fly horizontally, bounce off obstacles. */
function tickMultiTileBirdMovement(
    mouse: MouseEntity, size: CreatureSize,
    getTile: (x: number, y: number) => number,
): NestAbsorption | null {
    const nextX = mouse.x + mouse.facing;
    if (canMultiTileFlyTo(nextX, mouse.y, mouse.facing, size, getTile)) {
        mouse.x = nextX;
        return null;
    }
    // Turn in place — move next tick
    mouse.facing = -mouse.facing;
    return null;
}

/** Multi-tile fish movement: swim horizontally in water, bounce off non-water. */
function tickMultiTileFishMovement(
    mouse: MouseEntity, size: CreatureSize,
    getTile: (x: number, y: number) => number,
): NestAbsorption | null {
    const nextX = mouse.x + mouse.facing;
    if (allBodyTilesInWater(nextX, mouse.y, mouse.facing, size, getTile)) {
        mouse.x = nextX;
        return null;
    }
    // Turn in place — move next tick
    mouse.facing = -mouse.facing;
    return null;
}

/**
 * Bird movement: fly horizontally 1 tile/tick, bounce off solid walls.
 * No gravity — birds stay at their Y level. Absorbed by RATS_NEST on contact.
 */
function tickBirdMovement(mouse: MouseEntity, getTile: (x: number, y: number) => number): NestAbsorption | null {
    const nextX = mouse.x + mouse.facing;
    const frontTile = getTile(nextX, mouse.y) as TileType;

    // Check for nest absorption
    if (frontTile === TileType.RATS_NEST) {
        return { mouseId: mouse.id, nestX: nextX, nestY: mouse.y, rodentType: mouse.rodentType, facing: mouse.facing };
    }

    // Fly forward if passable
    if (isPassable(frontTile)) {
        mouse.x = nextX;
        return null;
    }

    // Hit a wall — bounce
    mouse.facing = -mouse.facing;
    const reverseX = mouse.x + mouse.facing;
    const reverseTile = getTile(reverseX, mouse.y) as TileType;

    if (reverseTile === TileType.RATS_NEST) {
        return { mouseId: mouse.id, nestX: reverseX, nestY: mouse.y, rodentType: mouse.rodentType, facing: mouse.facing };
    }

    if (isPassable(reverseTile)) {
        mouse.x = reverseX;
    }
    return null;
}

/**
 * Fish movement: swim horizontally 1 tile/tick in water, bounce off non-water.
 * Fish die if their current tile is not water (handled by tickMiceSuffocation).
 * Movement only proceeds into water tiles.
 */
function tickFishMovement(mouse: MouseEntity, getTile: (x: number, y: number) => number): NestAbsorption | null {
    const nextX = mouse.x + mouse.facing;
    const frontTile = getTile(nextX, mouse.y) as TileType;

    // Check for nest absorption
    if (frontTile === TileType.RATS_NEST) {
        return { mouseId: mouse.id, nestX: nextX, nestY: mouse.y, rodentType: mouse.rodentType, facing: mouse.facing };
    }

    // Swim forward only into water
    if (frontTile === TileType.WATER || frontTile === TileType.WATER_SURFACE) {
        mouse.x = nextX;
        return null;
    }

    // Not water ahead — bounce
    mouse.facing = -mouse.facing;
    const reverseX = mouse.x + mouse.facing;
    const reverseTile = getTile(reverseX, mouse.y) as TileType;

    if (reverseTile === TileType.RATS_NEST) {
        return { mouseId: mouse.id, nestX: reverseX, nestY: mouse.y, rodentType: mouse.rodentType, facing: mouse.facing };
    }

    if (reverseTile === TileType.WATER || reverseTile === TileType.WATER_SURFACE) {
        mouse.x = reverseX;
    }
    return null;
}

/** Apply gravity: fall 1 tile if no ground below. Mutates in place.
 *  Skips flying (bird) and swimming (fish) creatures — they don't obey gravity.
 *  Multi-tile creatures check all bottom-row body tiles for ground. */
export function tickMiceFalling(mice: MouseEntity[], getTile: (x: number, y: number) => number): void {
    for (const mouse of mice) {
        if (!mouse.alive) continue;
        const ai = getCreatureAI(mouse.rodentType);
        // Flying and swimming creatures don't obey gravity
        if (ai === 'fly' || ai === 'swim') continue;
        // Squirrels clinging to a wall or ceiling don't fall
        if (ai === 'cling' && squirrelHasSurface(mouse, getTile)) {
            continue;
        }

        const size = getCreatureSize(mouse.rodentType);
        const isMultiTile = size.width > 1 || size.height > 1;

        if (isMultiTile) {
            // Multi-tile: check if grounded (any bottom-row body tile has ground below)
            if (!hasGroundBelow(mouse.x, mouse.y, mouse.facing, size, getTile)) {
                // Also verify all body tiles at y+1 are passable (can actually fall)
                if (allBodyTilesPassable(mouse.x, mouse.y + 1, mouse.facing, size, getTile)) {
                    mouse.y += 1;
                }
            }
        } else {
            // 1×1: original single-tile check
            const belowTile = getTile(mouse.x, mouse.y + 1) as TileType;
            if (isPassable(belowTile)) {
                mouse.y += 1;
                // If squirrel falls, reset to floor-walking state
                if (ai === 'cling') {
                    mouse.wallDir = 0;
                    mouse.vertDir = 1;
                }
            }
        }
    }
}

/** Run combat: swords/slashes kill mice, mice damage players. Does NOT filter the array.
 *  Multi-tile creatures: all body tiles (cell>0) are checked for sword/slash hits and player overlap. */
export function tickMiceCombat(
    mice: MouseEntity[],
    players: SimulationPlayer[],
    activeSlashes: ActiveSlash[],
): MouseEvent[] {
    const events: MouseEvent[] = [];

    for (const mouse of mice) {
        if (!mouse.alive) continue;

        const size = getCreatureSize(mouse.rodentType);
        const isMultiTile = size.width > 1 || size.height > 1;
        // Pre-compute body tiles for multi-tile creatures
        const bodyTiles = isMultiTile
            ? getCreatureBodyTiles(mouse.x, mouse.y, mouse.facing, size)
            : null;

        // 1. Sword overlap kills mouse
        for (const player of players) {
            if (player.hp <= 0 || !player.isHoldingSword) continue;
            const swordX = player.x + player.facing * SWORD_OFFSET;
            const height = player.isCrouching ? PLAYER_HEIGHT_CROUCHING : PLAYER_HEIGHT_STANDING;

            let hit = false;
            if (bodyTiles) {
                // Multi-tile: check if sword overlaps any body tile
                for (const t of bodyTiles) {
                    if (t.tx === swordX && t.ty >= player.y && t.ty < player.y + height) {
                        hit = true;
                        break;
                    }
                }
            } else {
                hit = mouse.x === swordX && mouse.y >= player.y && mouse.y < player.y + height;
            }
            if (hit) {
                mouse.alive = false;
                events.push({ type: 'mouseDied', mouseId: mouse.id });
                break;
            }
        }
        if (!mouse.alive) continue;

        // 2. Slash overlap kills mouse
        for (const slash of activeSlashes) {
            let hit = false;
            if (bodyTiles) {
                for (const t of bodyTiles) {
                    if (t.tx === slash.x && t.ty === slash.y) {
                        hit = true;
                        break;
                    }
                }
            } else {
                hit = mouse.x === slash.x && mouse.y === slash.y;
            }
            if (hit) {
                mouse.alive = false;
                events.push({ type: 'mouseDied', mouseId: mouse.id });
                break;
            }
        }
        if (!mouse.alive) continue;

        // 3. Mouse overlaps player → player takes damage
        for (const player of players) {
            if (player.hp <= 0) continue;
            const height = player.isCrouching ? PLAYER_HEIGHT_CROUCHING : PLAYER_HEIGHT_STANDING;

            let overlaps = false;
            if (bodyTiles) {
                // Multi-tile: any body tile overlapping any player tile
                for (const t of bodyTiles) {
                    if (t.tx === player.x && t.ty >= player.y && t.ty < player.y + height) {
                        overlaps = true;
                        break;
                    }
                }
            } else {
                overlaps = mouse.x === player.x && mouse.y >= player.y && mouse.y < player.y + height;
            }
            if (overlaps) {
                player.hp -= MOUSE_DAMAGE;
                events.push({ type: 'mousePlayerDamage', playerId: player.id, hp: player.hp });
                if (player.hp <= 0) {
                    events.push({
                        type: 'mousePlayerDeath',
                        playerId: player.id,
                        deathX: player.x,
                        deathY: player.y + 1,
                    });
                }
            }
        }
    }

    return events;
}

/** Remove dead mice from an array in place (returns the filtered array). */
export function removeDeadMice(mice: MouseEntity[]): MouseEntity[] {
    return mice.filter(m => m.alive);
}

// ============================================================
// NEST RESPAWN POSITION COMPUTATION
// Shared by GameRoom (online) and LocalGameServer (offline).
// ============================================================

/**
 * Compute where a rodent should spawn when exiting a nest.
 * Checks both horizontal neighbors of the nest for passability,
 * preferring the side the rodent originally entered from.
 * Returns null if both sides are blocked (caller should re-queue).
 *
 * For squirrels, the facing direction is preserved as-is from entry.
 * For mice/rats, facing is set to point away from the nest.
 */
export function computeNestRespawnPosition(
    resp: NestRespawn,
    getTile: (x: number, y: number) => number,
): { x: number; y: number; facing: number; wallDir?: number; vertDir?: number } | null {
    const { nestX, nestY } = resp;

    // Determine which side the rodent entered from.
    // For mouse/rat: facing pointed INTO the nest, so entry side = nestX - facing.
    // For squirrel on a wall (wallDir != 0): entry side = nestX - wallDir.
    // For squirrel on floor/ceiling (wallDir == 0): use facing as horizontal hint.
    const ai = getCreatureAI(resp.rodentType);
    let preferredDir: number;
    if (ai === 'cling' && resp.wallDir !== undefined && resp.wallDir !== 0) {
        preferredDir = -resp.wallDir;
    } else {
        preferredDir = -resp.facing;
    }

    const preferredX = nestX + preferredDir;
    const otherX = nestX - preferredDir;
    const preferredOpen = isPassable(getTile(preferredX, nestY) as TileType);
    const otherOpen = isPassable(getTile(otherX, nestY) as TileType);

    let spawnX: number;
    if (preferredOpen) {
        spawnX = preferredX;
    } else if (otherOpen) {
        spawnX = otherX;
    } else {
        return null; // Both sides blocked — keep in nest
    }

    // Facing: squirrels preserve their original facing, others face away from nest
    const exitDir = spawnX - nestX; // +1 or -1: direction from nest to spawn
    const spawnFacing = ai === 'cling' ? resp.facing : exitDir;

    // Squirrels exit on the floor with preserved facing; wallDir/vertDir reset to ground
    const wallDir = ai === 'cling' ? 0 : undefined;
    const vertDir = ai === 'cling' ? 1 : undefined;

    return { x: spawnX, y: nestY, facing: spawnFacing, wallDir, vertDir };
}

// ============================================================
// MOUSE SIMULATION CLASS
// Backward-compatible wrapper used by LocalGameServer and GameScene.
// ============================================================

export class MouseSimulation {
    mice: MouseEntity[] = [];
    private nextMouseId: number = 0;

    spawnMouse(x: number, y: number, facing: number, rodentType: string = RodentType.MOUSE): MouseEntity {
        const mouse: MouseEntity = {
            id: this.nextMouseId++,
            x,
            y,
            facing,
            alive: true,
            rodentType,
        };
        this.mice.push(mouse);
        return mouse;
    }

    tickSuffocation(getTile: (x: number, y: number) => number): MouseEvent[] {
        const events = tickMiceSuffocation(this.mice, getTile);
        this.mice = removeDeadMice(this.mice);
        return events;
    }

    tickMovement(getTile: (x: number, y: number) => number): NestAbsorption[] {
        const absorptions = tickMiceMovement(this.mice, getTile);
        // Remove absorbed mice from the list
        if (absorptions.length > 0) {
            this.mice = removeDeadMice(this.mice);
        }
        return absorptions;
    }

    tickFalling(getTile: (x: number, y: number) => number): void {
        tickMiceFalling(this.mice, getTile);
    }

    tickCombat(
        players: SimulationPlayer[],
        activeSlashes: ActiveSlash[],
    ): MouseEvent[] {
        const events = tickMiceCombat(this.mice, players, activeSlashes);
        this.mice = removeDeadMice(this.mice);
        return events;
    }

    removeMouse(id: number): void {
        const mouse = this.mice.find(m => m.id === id);
        if (mouse) {
            mouse.alive = false;
        }
        this.mice = removeDeadMice(this.mice);
    }

    getMice(): MouseEntity[] {
        return this.mice.filter(m => m.alive);
    }

    clear(): void {
        this.mice = [];
        this.nextMouseId = 0;
    }
}
